/**
 * Run Stream Event Types
 *
 * Types for SSE events from GET /v1/runs/{runId}/stream
 * Used for real-time workflow execution visualization.
 */

// ============================================================================
// Event Type Discriminator
// ============================================================================

export type RunStreamEventType =
  | "run_started"
  | "run_completed"
  | "run_status_changed"
  | "node_started"
  | "node_waiting"
  | "node_scheduled"
  | "node_auto_swapping"
  | "node_completed"
  | "node_failed"
  | "node_skipped"
  | "for_each_started"
  | "for_each_iteration_done"
  | "for_each_completed";

// ============================================================================
// Base Event Interface
// ============================================================================

interface BaseRunStreamEvent {
  type: RunStreamEventType;
  runId: string;
  timestamp: string; // ISO 8601
}

// ============================================================================
// Run Lifecycle Events
// ============================================================================

export interface RunStartedEvent extends BaseRunStreamEvent {
  type: "run_started";
  workflowId?: string;
}

export interface RunCompletedEvent extends BaseRunStreamEvent {
  type: "run_completed";
  status: "success" | "partial" | "failure" | "cancelled";
  startedAt?: string; // Server timestamp for accurate duration
  finishedAt?: string; // Server timestamp for accurate duration
  totalCu?: string; // Total CU consumed by this run (decimal as string)
}

export interface RunStatusChangedEvent extends BaseRunStreamEvent {
  type: "run_status_changed";
  status: "running" | "waiting";
}

// ============================================================================
// Node Execution Events
// ============================================================================

export interface NodeStartedEvent extends BaseRunStreamEvent {
  type: "node_started";
  nodeId: string;
  nodeType?: string;
}

export interface NodeWaitingEvent extends BaseRunStreamEvent {
  type: "node_waiting";
  nodeId: string;
  nodeType?: string;
}

export interface NodeCompletedEvent extends BaseRunStreamEvent {
  type: "node_completed";
  nodeId: string;
  nodeType?: string;
  input?: Record<string, unknown>;
  result?: Record<string, unknown>;
}

export interface NodeFailedEvent extends BaseRunStreamEvent {
  type: "node_failed";
  nodeId: string;
  nodeType?: string;
  error?: string;
  /** Structured error info from the backend (new runs only). */
  errorInfo?: ErrorInfo;
}

export interface NodeAutoSwappingEvent extends BaseRunStreamEvent {
  type: "node_auto_swapping";
  nodeId: string;
  nodeType?: string;
}

export interface NodeScheduledEvent extends BaseRunStreamEvent {
  type: "node_scheduled";
  nodeId: string;
  nodeType?: string;
  scheduledAt: string; // ISO 8601 — when this node is scheduled to execute
}

export interface NodeSkippedEvent extends BaseRunStreamEvent {
  type: "node_skipped";
  nodeId: string;
  nodeType?: string;
}

// ============================================================================
// For-Each Loop Events
// ============================================================================

export interface ForEachStartedEvent extends BaseRunStreamEvent {
  type: "for_each_started";
  nodeId: string;
  forEachTotal: number;
}

export interface ForEachIterationDoneEvent extends BaseRunStreamEvent {
  type: "for_each_iteration_done";
  nodeId: string;
  forEachIndex: number; // 0-based
  forEachTotal: number;
  loopBodyNodeIds?: string[];
}

export interface ForEachCompletedEvent extends BaseRunStreamEvent {
  type: "for_each_completed";
  nodeId: string;
  /**
   * Final terminal status of the for-each node. `partial` is emitted when
   * iterations had mixed outcomes (some succeeded, some failed) and the
   * outer for-each itself didn't fail outright.
   */
  status: "success" | "partial" | "failure";
}

// ============================================================================
// Union Type for All Events
// ============================================================================

export type RunStreamEvent =
  | RunStartedEvent
  | RunCompletedEvent
  | RunStatusChangedEvent
  | NodeStartedEvent
  | NodeWaitingEvent
  | NodeAutoSwappingEvent
  | NodeScheduledEvent
  | NodeCompletedEvent
  | NodeFailedEvent
  | NodeSkippedEvent
  | ForEachStartedEvent
  | ForEachIterationDoneEvent
  | ForEachCompletedEvent;

// ============================================================================
// Node Execution State (for UI visualization)
// ============================================================================

/**
 * Possible execution statuses for a node
 */
export type NodeExecutionStatus =
  | "idle"
  | "running"
  | "confirming"
  | "waiting"
  | "scheduled"
  | "auto_swapping"
  | "success"
  | "partial"
  | "failure"
  | "skipped";

/**
 * Execution state for a single node
 */
export interface NodeExecutionState {
  status: NodeExecutionStatus;
  startedAt?: string;
  finishedAt?: string;
  scheduledAt?: string; // When this node is scheduled to execute
  input?: Record<string, unknown>;
  result?: Record<string, unknown>;
  error?: string;
  /** Structured error info from the backend (new runs only). */
  errorInfo?: ErrorInfo;
  // For for-each nodes
  forEachProgress?: {
    current: number; // 1-based for display
    total: number;
  };
  forEachCounts?: {
    succeeded: number;
    failed: number;
  };
}

/**
 * Map of node IDs to their execution states
 */
export type NodesExecutionState = Record<string, NodeExecutionState>;

// ============================================================================
// Overall Run State
// ============================================================================

/**
 * Status of the overall run
 */
export type RunStatus =
  | "connecting"
  | "pending"
  | "running"
  | "waiting"
  | "success"
  | "partial"
  | "failure"
  | "cancelled";

export type TerminalRunStatus = "success" | "partial" | "failure" | "cancelled";
export type DegradedRunStatus = "partial" | "failure";

const TERMINAL_RUN_STATUSES: ReadonlySet<RunStatus> = new Set<RunStatus>([
  "success",
  "partial",
  "failure",
  "cancelled",
]);

export function isTerminalRunStatus(status: RunStatus | undefined | null): status is TerminalRunStatus {
  return !!status && TERMINAL_RUN_STATUSES.has(status);
}

/**
 * A run is "degraded" when it surfaces at least one user-visible failure
 * — either a hard `failure` (top-level node failed) or a `partial` (some
 * for-each iterations failed). Mirrors entity.RunStatus.IsDegraded on
 * the Go side.
 */
export function isDegradedRunStatus(status: RunStatus | undefined | null): status is DegradedRunStatus {
  return status === "failure" || status === "partial";
}

/**
 * Source of a run being visualized in the editor.
 * Button runs come from the user's explicit Test/Run action in the editor UI.
 * Assistant runs are launched from AI chat suggestions/actions.
 * Autorun runs are launched programmatically after redirects.
 * Background runs come from live trigger activity auto-subscribed by the editor.
 */
export type RunExecutionSource = "button" | "assistant" | "autorun" | "background";

/**
 * Only explicit editor button clicks should surface blocking success/error UI.
 */
export function shouldShowBlockingRunFeedback(source?: RunExecutionSource): boolean {
  return source === "button";
}

/**
 * Complete execution state for a run
 */
export interface RunExecutionState {
  runId: string;
  workflowId?: string;
  status: RunStatus;
  startedAt?: string;
  finishedAt?: string;
  nodeStates: NodesExecutionState;
  isSimulation?: boolean;
  source: RunExecutionSource;
}

// ============================================================================
// Event Validation
// ============================================================================

/**
 * Valid event types for runtime validation
 */
const VALID_EVENT_TYPES: RunStreamEventType[] = [
  "run_started",
  "run_completed",
  "run_status_changed",
  "node_started",
  "node_waiting",
  "node_scheduled",
  "node_auto_swapping",
  "node_completed",
  "node_failed",
  "node_skipped",
  "for_each_started",
  "for_each_iteration_done",
  "for_each_completed",
];

/**
 * Validates that a value is a non-empty string
 */
function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.length > 0;
}

/**
 * Validates the base structure of a run stream event
 */
function isValidBaseEvent(event: unknown): event is { type: string; runId: string; timestamp: string } {
  if (typeof event !== "object" || event === null) return false;
  const e = event as Record<string, unknown>;
  return isNonEmptyString(e.type) && isNonEmptyString(e.runId) && isNonEmptyString(e.timestamp);
}

/**
 * Validates a run stream event and returns it typed, or null if invalid
 *
 * @param event - The unknown event data to validate
 * @returns The validated event or null if invalid
 *
 * @example
 * ```ts
 * const parsed = JSON.parse(data);
 * const event = validateRunStreamEvent(parsed);
 * if (event) {
 *   handleEvent(event);
 * } else {
 *   console.warn("Invalid event received");
 * }
 * ```
 */
export function validateRunStreamEvent(event: unknown): RunStreamEvent | null {
  // Check base structure
  if (!isValidBaseEvent(event)) {
    return null;
  }

  // Check event type is valid
  if (!VALID_EVENT_TYPES.includes(event.type as RunStreamEventType)) {
    return null;
  }

  const e = event as Record<string, unknown>;

  // Type-specific validation
  switch (event.type) {
    case "run_started":
      // Optional workflowId
      return event as RunStartedEvent;

    case "run_completed":
      if (!isTerminalRunStatus(e.status as RunStatus | undefined)) {
        return null;
      }
      return event as RunCompletedEvent;

    case "run_status_changed":
      if (e.status !== "running" && e.status !== "waiting") {
        return null;
      }
      return event as RunStatusChangedEvent;

    case "node_started":
    case "node_waiting":
    case "node_auto_swapping":
    case "node_completed":
    case "node_failed":
    case "node_skipped":
      if (!isNonEmptyString(e.nodeId)) {
        return null;
      }
      return event as
        | NodeStartedEvent
        | NodeWaitingEvent
        | NodeAutoSwappingEvent
        | NodeCompletedEvent
        | NodeFailedEvent
        | NodeSkippedEvent;

    case "node_scheduled":
      if (!isNonEmptyString(e.nodeId) || !isNonEmptyString(e.scheduledAt)) {
        return null;
      }
      return event as NodeScheduledEvent;

    case "for_each_started":
      if (!isNonEmptyString(e.nodeId) || typeof e.forEachTotal !== "number") {
        return null;
      }
      return event as ForEachStartedEvent;

    case "for_each_iteration_done":
      if (!isNonEmptyString(e.nodeId) || typeof e.forEachIndex !== "number" || typeof e.forEachTotal !== "number") {
        return null;
      }
      return event as ForEachIterationDoneEvent;

    case "for_each_completed":
      if (!isNonEmptyString(e.nodeId) || (e.status !== "success" && e.status !== "partial" && e.status !== "failure")) {
        return null;
      }
      return event as ForEachCompletedEvent;

    default:
      return null;
  }
}

// ============================================================================
// Error Info (structured error from backend)
// ============================================================================

/**
 * Categories used to classify backend errors. The frontend uses these to
 * decide on iconography, color, and copy.
 */
export type ErrorCategory =
  | "funds"
  | "amount"
  | "config"
  | "auth"
  | "network"
  | "service"
  | "onchain"
  | "validation"
  | "internal";

/**
 * Structured error information surfaced from the backend on `node_failed`
 * events. Older runs that predate this field fall back to the legacy
 * `error` string + client-side `summarizeError()` regex.
 */
export interface ErrorInfo {
  code: string;
  category: ErrorCategory;
  displayMessage: string;
  remediation?: string;
  retryable: boolean;
  details?: unknown;
  rawMessage: string;
}
